import {
  Button,
  DateInput,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  getResolver,
  Label,
  RadioGroup,
  RadioGroupItem,
  toast,
} from '@folio/components';
import { formatPrice } from '@folio/lib';
import type { QueryObserverResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import IsSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore';
import {
  type Claim,
  type ClaimDto,
  type MissingTenantErrorDto,
  type ResponseErrorConfig,
  useClaimsControllerDelete,
  useClaimsControllerUpdate,
} from 'korint-sdk';
import type { DateField } from 'korint-sdk/react';
import { Trash } from 'lucide-react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Group, Stack } from '@folio/components/ui/flex';
import { StatusPill } from '@folio/components/ui/status-pill';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

dayjs.extend(IsSameOrBeforePlugin);

export function PolicyClaimItem({
  title,
  claim,
  hasResponsibility,
  isHistoricalClaim,
  isReadonly,
  fromDate,
  toDate,
  refetchClaims,
}: {
  claim: Claim;
  title: string;
  hasResponsibility?: boolean;
  isHistoricalClaim?: boolean;
  isReadonly?: boolean;
  fromDate?: Date;
  toDate?: Date;
  refetchClaims: () => Promise<
    QueryObserverResult<ClaimDto[], ResponseErrorConfig<MissingTenantErrorDto>>
  >;
}) {
  const { t } = useTranslation();
  const itemId = useId();

  const { mutateAsync: updateClaim } = useClaimsControllerUpdate({
    mutation: {
      onSuccess: () => refetchClaims(),
      onError: () => {
        toast({
          description: t('policyClaims.updateError'),
        });
      },
    },
  });

  const { mutate: deleteClaim, isPending: isDeleting } = useClaimsControllerDelete({
    mutation: {
      onSuccess: () => refetchClaims(),
      onError: () => {
        toast({
          description: t('policyClaims.deleteError'),
        });
      },
    },
  });

  const occurredAtField: DateField = {
    name: 'occurredAt',
    type: 'date',
    required: false,
    min: fromDate ? dayjs(fromDate).format('YYYY-MM-DD') : undefined,
    max: toDate ? dayjs(toDate).format('YYYY-MM-DD') : undefined,
  };

  const form = useForm({
    values: { occurredAt: claim.occurredAt ? dayjs(claim.occurredAt).format('YYYY-MM-DD') : '' },
    resolver: getResolver([occurredAtField]),
  });

  return (
    <Stack gap={6} className="p-6">
      <Group align="stretch" justify="between" gap={0} className="text-md font-medium">
        <div>{title}</div>
        <Group justify="between" gap={2} className="text-md font-medium">
          {isHistoricalClaim && (
            <StatusPill color="bg-slate-200 text-amber-600" label={t('policyClaims.historical')} />
          )}
          {!isReadonly && (
            <Button
              type="button"
              variant="destructive-ghost"
              size="xs"
              disabled={isDeleting}
              loading={isDeleting}
              onClick={() => deleteClaim(claim)}
              icon={<Trash className="h-3" />}
            >
              {t('policyClaims.delete')}
            </Button>
          )}
        </Group>
      </Group>
      {claim.reserve !== undefined && (
        <div>
          <Label>{t('policyClaims.reserve', { amount: formatPrice(claim.reserve) })}</Label>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        {hasResponsibility ? (
          <div>
            <Label className="mb-4">{t('policyClaims.responsibility')}</Label>
            <RadioGroup
              className="pt-3"
              value={claim.responsibility ?? ''}
              disabled={isReadonly}
              onValueChange={(e: string) =>
                updateClaim({ id: claim.id, data: { responsibility: e } })
              }
            >
              <Group gap={8}>
                <RadioGroupItem value="NONE" id={`${itemId}NONE`} disabled={isReadonly} />
                <Label htmlFor={`${itemId}NONE`}>{t('claimResponsibility.NONE')}</Label>
              </Group>
              <Group gap={8}>
                <RadioGroupItem value="HALF" id={`${itemId}HALF`} disabled={isReadonly} />
                <Label htmlFor={`${itemId}HALF`}>{t('claimResponsibility.HALF')}</Label>
              </Group>
              <Group gap={8}>
                <RadioGroupItem value="FULL" id={`${itemId}FULL`} disabled={isReadonly} />
                <Label htmlFor={`${itemId}FULL`}>{t('claimResponsibility.FULL')}</Label>
              </Group>
            </RadioGroup>
          </div>
        ) : null}
        <div>
          <Form {...form}>
            <FormField
              name="occurredAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('policyClaims.occurredAt')}</FormLabel>
                  <FormControl>
                    <DateInput
                      value={field.value}
                      disabled={isReadonly}
                      onChange={date => {
                        form.setValue(field.name, date ?? '');

                        if (date) {
                          form.handleSubmit(async ({ occurredAt }) => {
                            await updateClaim({
                              id: claim.id,
                              data: {
                                occurredAt: dayjs(occurredAt).utc().toISOString(),
                              },
                            });
                          })();
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Form>
        </div>
      </div>
    </Stack>
  );
}
